/**
 * Troubleshooting Articles — Russian
 */

import type { HelpArticle } from "../../../types";

export const TROUBLESHOOTING_ARTICLES_RU: HelpArticle[] = [
  {
    id: "ts-001",
    slug: "kyc-verification-issues",
    title: "Проблемы с KYC-верификацией",
    description: "Решение проблем с проверкой личности",
    category: "troubleshooting",
    popular: true,
    order: 1,
    keywords: ["kyc", "верификация", "ошибка", "отклонено", "проблема"],
    content: `
# Проблемы с KYC-верификацией

Возникли проблемы с верификацией? Вот как решить типичные проблемы.

## Частые причины отклонения

### Проблемы с документом
| Проблема | Решение |
|----------|---------|
| Размытое фото | Переснимите при хорошем освещении |
| Блик на документе | Избегайте прямого света |
| Просроченный документ | Используйте действительный |
| Обрезаны углы | Покажите все 4 угла |
| Неверный тип документа | Паспорт/ID/права |

### Проблемы с селфи
| Проблема | Решение |
|----------|---------|
| Лицо не видно | Снимите очки, смотрите в камеру |
| Плохое освещение | Используйте естественный свет |
| Не совпадает с документом | Убедитесь, что это вы |
| Глаза закрыты | Держите глаза открытыми |
| Лицо закрыто | Снимите маску/шапку |

## Повторная попытка

### 1. Проверьте требования
- Действительный, не просроченный документ
- Оригинал (не копия)
- Чёткий, читаемый текст
- Весь документ виден

### 2. Подготовьте условия
- Хорошее освещение (лучше естественное)
- Стабильная камера/телефон
- Чистый объектив камеры
- Однотонный фон

### 3. Сделайте новые фото
- Держите ровно, не торопитесь
- Заполните кадр документом
- Без вспышки (даёт блики)
- Проверьте перед отправкой

## Всё ещё не получается?

### Ручная проверка
1. Откройте Настройки > Верификация
2. Нажмите «Запросить ручную проверку»
3. Загрузите документы
4. Добавьте пояснение при необходимости
5. Ожидайте 24–48 часов

### Обратитесь в поддержку
Напишите на support@supabank.xyz:
- Email аккаунта
- Описание проблемы
- Скриншоты ошибок
- Фото, которые пытаетесь загрузить

## Что означают статусы

| Статус | Значение |
|--------|----------|
| Ожидание | Обрабатывается |
| Требуется информация | Нужны дополнительные документы |
| Отклонено | Смотрите причину, попробуйте снова |
| Одобрено | Верификация завершена |
    `.trim(),
  },
  {
    id: "ts-002",
    slug: "bank-account-connection-problems",
    title: "Проблемы с привязкой банка",
    description: "Решение проблем с добавлением счёта",
    category: "troubleshooting",
    order: 2,
    keywords: ["банк", "счёт", "привязка", "ошибка", "подключение"],
    content: `
# Проблемы с привязкой банка

Проблемы с подключением счёта? Вот решения.

## Частые проблемы

### Неверные реквизиты
**Симптомы**: Счёт сразу отклоняется

**Решение**:
1. Перепроверьте routing number (9 цифр)
2. Проверьте номер счёта
3. Подтвердите тип счёта
4. Имя должно совпадать точно

### Верификация микродепозитами не прошла
**Симптомы**: Не удаётся подтвердить суммами

**Решение**:
1. Подождите 2–3 рабочих дня для зачисления
2. Проверьте правильный счёт
3. Примечание: суммы в центах, не долларах
4. Попробуйте снова с точными суммами

### Несовпадение имени
**Симптомы**: Счёт отклонён, имя не совпадает

**Решение**:
- Счёт должен быть на ваше имя
- Используйте имя точно как в банке
- Совместные счета могут не работать
- Обратитесь в поддержку за помощью

## Способы верификации

### Способ 1: Микродепозиты
1. Мы отправляем два мелких перевода
2. Проверьте свой банк (2–3 дня)
3. Введите суммы в Supabank
4. Счёт подтверждён

### Способ 2: Мгновенная верификация
1. Подключитесь через Plaid (если доступно)
2. Войдите в свой банк
3. Мгновенная верификация
4. Более безопасно

### Способ 3: Загрузка документа
1. Загрузите банковскую выписку
2. Должно быть видно:
   - Ваше имя
   - Номер счёта
   - Свежая дата
3. Проверка в течение 24 часов

## Проблемы с конкретными банками

### Chase
- Используйте полный номер счёта (не сокращённый)
- Личные счета работают лучше

### Bank of America
- Routing зависит от штата
- Проверьте routing на бумажном чеке

### Wells Fargo
- Используйте routing для checking, не savings
- Убедитесь, что счёт активен

## Всё ещё проблемы?

Обратитесь в поддержку:
- Название банка
- Сообщение об ошибке
- Скриншот проблемы
- (Не отправляйте полные номера счетов)
    `.trim(),
  },
  {
    id: "ts-003",
    slug: "delayed-transactions",
    title: "Задержка транзакций",
    description: "Что делать, если транзакция идёт слишком долго",
    category: "troubleshooting",
    order: 3,
    keywords: ["задержка", "медленно", "ожидание", "застряла", "pending"],
    content: `
# Задержка транзакций

Транзакция идёт дольше ожидаемого? Вот что проверить.

## Задержка депозитов

### Проверьте статус в блокчейне
1. Получите хеш транзакции
2. Откройте эксплорер (Etherscan и т.д.)
3. Проверьте подтверждения
4. Посмотрите, не застряла ли в mempool

### Частые причины

| Причина | Решение |
|---------|---------|
| Загруженность сети | Дождитесь подтверждения |
| Низкая комиссия за газ | Может занять часы |
| Неправильная сеть | Обратитесь в поддержку |
| Ниже минимума | Может не обработаться |

### Ожидаемые сроки
- Ethereum: 2–5 минут
- Polygon: 3–8 минут
- Solana: 15–30 секунд
- При загруженности: значительно дольше

## Задержка выплат

### Проверьте статус в Supabank
1. Откройте «Транзакции»
2. Нажмите на выплату
3. Проверьте подробный статус
4. Смотрите ориентировочное время

### Частые причины

| Причина | Решение |
|---------|---------|
| Первая выплата | Может требовать проверки |
| Крупная сумма | Дополнительная верификация |
| Обработка банком | Ждите банк |
| Выходные/праздники | Банки не работают |

### Ожидаемые сроки
- ACH: 2–3 рабочих дня
- Wire: 1–2 рабочих дня
- SEPA: 1 рабочий день

## Когда обращаться в поддержку

### Депозиты
- Подтверждено в блокчейне, но не отображается (> 1 часа)
- Статус застрял на «Конвертация» (> 30 минут)
- Неверная сумма

### Выплаты
- «Отправлено» более 3 рабочих дней (ACH)
- «В пути» более 5 рабочих дней
- Возвращено без объяснения

## Обращение в поддержку

Укажите:
- ID транзакции
- Ожидаемые vs фактические сроки
- Хеш блокчейна (депозиты)
- Скриншоты
    `.trim(),
  },
  {
    id: "ts-004",
    slug: "error-codes-explained",
    title: "Коды ошибок",
    description: "Что означают сообщения об ошибках",
    category: "troubleshooting",
    order: 4,
    keywords: ["ошибка", "код", "сообщение", "проблема"],
    content: `
# Коды ошибок

Понимание сообщений об ошибках помогает быстрее решать проблемы.

## Частые коды ошибок

### Ошибки аутентификации

| Код | Значение | Решение |
|-----|----------|---------|
| AUTH_001 | Сессия истекла | Войдите снова |
| AUTH_002 | Недействительная magic link | Запросите новую |
| AUTH_003 | Требуется 2FA | Введите код 2FA |
| AUTH_004 | Слишком много попыток | Подождите 30 минут |

### Ошибки транзакций

| Код | Значение | Решение |
|-----|----------|---------|
| TXN_001 | Недостаточно средств | Проверьте баланс |
| TXN_002 | Ниже минимума | Увеличьте сумму |
| TXN_003 | Превышен лимит | Уменьшите или повысьте уровень |
| TXN_004 | Неверный адрес | Проверьте формат адреса |
| TXN_005 | Неподдерживаемая сеть | Используйте поддерживаемую |

### Ошибки банковского счёта

| Код | Значение | Решение |
|-----|----------|---------|
| BANK_001 | Неверный routing | Проверьте routing number |
| BANK_002 | Неверный счёт | Проверьте номер счёта |
| BANK_003 | Несовпадение имени | Используйте счёт на ваше имя |
| BANK_004 | Верификация не прошла | Повторите верификацию |

### Ошибки верификации

| Код | Значение | Решение |
|-----|----------|---------|
| KYC_001 | Документ нечёткий | Переснимите фото |
| KYC_002 | Документ просрочен | Используйте действительный |
| KYC_003 | Селфи не совпадает | Переснимите селфи |
| KYC_004 | Регион не поддерживается | Проверьте доступность |

## Формат сообщения об ошибке

\`\`\`
ОШИБКА: [КОД]
Сообщение: Понятное описание
Детали: Дополнительная информация
Действие: Рекомендуемый шаг
\`\`\`

## Если ошибка повторяется

1. Запишите код ошибки
2. Попробуйте рекомендуемое решение
3. Если не помогает, обратитесь в поддержку
4. Укажите: код, скриншот, что пробовали
    `.trim(),
  },
  {
    id: "ts-005",
    slug: "common-issues-faq",
    title: "Частые вопросы",
    description: "Быстрые ответы на типичные проблемы",
    category: "troubleshooting",
    order: 5,
    keywords: ["faq", "частые", "вопросы", "проблемы"],
    content: `
# Частые вопросы

Быстрые решения самых распространённых проблем.

## Проблемы со входом

**В: Magic link не приходит?**
О: Проверьте спам, правильность email, подождите 2 минуты и повторите.

**В: Magic link истёк?**
О: Ссылки действуют 15 минут. Запросите новую.

**В: Нет доступа к устройству с 2FA?**
О: Используйте резервные коды или обратитесь в поддержку для сброса.

## Проблемы с транзакциями

**В: Депозит не отображается?**
О: Проверьте подтверждение в блокчейне. Обратитесь в поддержку, если подтверждено > 1 часа назад.

**В: Выплата не получена?**
О: Проверьте статус в приложении. ACH занимает 2–3 рабочих дня. Обратитесь в поддержку через 5 дней.

**В: Зачислена неверная сумма?**
О: Проверьте курс на момент конвертации. Обратитесь в поддержку с ID транзакции.

## Проблемы с верификацией

**В: KYC постоянно не проходит?**
О: Хорошее освещение, действительный документ, чёткие фото. Запросите ручную проверку при повторных проблемах.

**В: Имя не совпадает?**
О: Используйте имя точно как в документе. Обратитесь в поддержку при смене имени.

## Проблемы с банковским счётом

**В: Счёт отклонён?**
О: Перепроверьте все данные. Счёт должен быть на ваше имя.

**В: Микродепозиты не пришли?**
О: Подождите 2–3 рабочих дня. Убедитесь, что проверяете правильный счёт.

## Общие проблемы

**В: Приложение не загружается?**
О: Очистите кэш, попробуйте другой браузер, проверьте интернет.

**В: Не видно недавние транзакции?**
О: Потяните вниз для обновления или нажмите кнопку обновления. Проверьте фильтры.

**В: Нужно сменить email?**
О: Откройте Настройки > Безопасность > Сменить email. Подтвердите старый и новый email.

## Нужна помощь?

Напишите на support@supabank.xyz с описанием проблемы.
    `.trim(),
  },
  {
    id: "ts-006",
    slug: "contacting-support",
    title: "Связь с поддержкой",
    description: "Как получить помощь от команды поддержки",
    category: "troubleshooting",
    popular: true,
    order: 6,
    keywords: ["поддержка", "помощь", "контакт", "связаться"],
    content: `
# Связь с поддержкой

Несколько способов получить помощь.

## Каналы поддержки

### AI-ассистент (самый быстрый)
- Нажмите на чат в приложении
- Доступен 24/7
- Мгновенные ответы
- Может создать тикет

### Email-поддержка
- support@supabank.xyz
- Ответ: в течение 24 часов
- Лучше для сложных вопросов
- Прикрепляйте скриншоты

### Центр помощи
- supabank.xyz/support
- Статьи самообслуживания
- Пошаговые инструкции
- Всегда доступен

## Что указывать

### По транзакциям
- ID транзакции
- Сумма и валюта
- Хеш блокчейна (для депозитов)
- Скриншоты

### По верификации
- Email аккаунта
- Сообщения об ошибках
- Фото, которые загружаете
- Информация об устройстве/браузере

### По банку
- Название банка
- Сообщение об ошибке
- (Не отправляйте номера счетов)

## Время ответа

| Приоритет | Время ответа |
|-----------|--------------|
| Безопасность | 1 час |
| Доступ к аккаунту | 4 часа |
| Транзакции | 24 часа |
| Общие вопросы | 24–48 часов |

## Советы для быстрого решения

**Лучшие практики:**
- Укажите все детали сразу
- Прикрепите скриншоты
- Один тикет на проблему
- Проверяйте спам на ответы
- Опишите проблему конкретно

## Эскалация

Если проблема не решена:
1. Ответьте на существующий тикет
2. Запросите эскалацию
3. Укажите, что уже пробовали
4. Ожидайте 48 часов для эскалированной проверки

## Вопросы безопасности

Для срочных вопросов безопасности:
- Email: security@supabank.xyz
- Укажите: email аккаунта, тип проблемы
- Ответ: в течение 1 часа
    `.trim(),
  },
  {
    id: "ts-007",
    slug: "system-status",
    title: "Статус системы",
    description: "Проверка работоспособности сервисов",
    category: "troubleshooting",
    order: 7,
    keywords: ["статус", "сбой", "недоступен", "обслуживание", "uptime"],
    content: `
# Статус системы

Проверьте, работают ли сервисы Supabank нормально.

## Страница статуса

Откройте: **status.supabank.xyz**

Показывает состояние в реальном времени:
- Основная платформа
- API-сервисы
- Обработка платежей
- Мониторинг блокчейна

## Компоненты системы

### Основная платформа
- Веб-приложение
- Аутентификация пользователей
- Функции личного кабинета

### Обработка транзакций
- Обнаружение депозитов
- Движок конвертации
- Обработка выплат

### Интеграции
- Ноды блокчейна
- Банковские партнёры
- KYC-провайдер

## Индикаторы статуса

| Статус | Значение |
|--------|----------|
| Работает | Всё в норме |
| Замедление | Медленнее обычного |
| Сбой | Сервис недоступен |
| Обслуживание | Плановые работы |

## Подписка на обновления

1. Откройте status.supabank.xyz
2. Нажмите «Подписаться»
3. Введите email
4. Получайте уведомления о:
   - Сбоях в работе
   - Окнах обслуживания
   - Обновлениях о решении

## Во время сбоя

### Что делать
- Сначала проверьте страницу статуса
- Не повторяйте транзакции многократно
- Дождитесь решения
- Следите за @supabank в Twitter

### Чего НЕ делать
Создавать несколько тикетов в поддержку
Повторять неудачные транзакции постоянно
Думать, что средства потеряны

## Плановое обслуживание

- Объявляется за 48 часов
- Обычно в часы низкой нагрузки
- Обычно кратко (< 30 минут)
- Обновления на странице статуса

## История доступности

- Цель: 99.9% uptime
- История инцидентов на странице статуса
- Публикуются отчёты после инцидентов
    `.trim(),
  },
  {
    id: "ts-008",
    slug: "scheduled-maintenance",
    title: "Плановое обслуживание",
    description: "О плановых технических работах",
    category: "troubleshooting",
    order: 8,
    keywords: ["обслуживание", "плановое", "простой", "работы"],
    content: `
# Плановое обслуживание

Как мы проводим плановые работы с минимальными неудобствами.

## Окна обслуживания

### Регулярное обслуживание
- Когда: воскресенье 2–4 UTC
- Длительность: обычно < 30 минут
- Влияние: минимальное или нет

### Крупные обновления
- Объявляются за 48+ часов
- Планируются в часы низкой нагрузки
- Возможен краткий перерыв в работе

## Что происходит во время работ

### Обычно затронуто
- Новые транзакции могут приостановиться
- Может отображаться страница обслуживания
- API может возвращать статус обслуживания

### Обычно не затронуто
- Ожидающие транзакции продолжаются
- Мониторинг блокчейна
- Банковские переводы в процессе

## Как мы уведомляем

### За 48+ часов
- Email-уведомление (если подписаны)
- Объявление на странице статуса
- Баннер в приложении

### Во время работ
- Страница статуса обновляется
- Показывается экран обслуживания
- Ориентировочное время завершения

### После завершения
- Страница статуса обновляется
- Нормальная работа восстановлена
- Публикуется сводка (для крупных работ)

## Что вам делать

### До работ
Завершите срочные транзакции
Не начинайте крупных операций
Запомните ожидаемую длительность

### Во время работ
Проверяйте страницу статуса на обновления
Терпеливо ждите
Не повторяйте операции многократно

### После работ
Обновите браузер
Проверьте ожидающие транзакции
Сообщите о проблемах

## Экстренное обслуживание

Иногда необходимо для:
- Патчей безопасности
- Критических исправлений
- Проблем инфраструктуры

Уведомление короче, но схема коммуникации аналогичная.

## Вопросы?

Напишите на support@supabank.xyz по вопросам обслуживания.
    `.trim(),
  },
];
